import {
  Box,
  Button,
  FieldWrapper,
  FlexBox,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { HookFormCheckbox, HookFormText } from 'lib-shared-hook-form';
import { MouseEventHandler, useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useToggle } from 'react-use';

import { emailsApi } from 'apis/emails';
import {
  HookFormEmailSelect,
  HookFormQueuesSelect,
  HookFormTagSelect,
} from 'components/hookForm';
// eslint-disable-next-line no-restricted-imports
import { ToggleBox, ToggleBoxBody, ToggleBoxHead } from 'fable/toggleBox';
import { createAppModal } from 'utils/modal';
import { useServerStorage } from 'utils/useServerStorage/useServerStorage';

import { ComposeEmailFieldEmailContact } from './components/composeEmailFieldEmailContact';
import { ComposeEmailFieldRichText } from './components/composeEmailFieldRichText';
import styles from './composeEmailModal.module.scss';
import type { FormValues } from './composeEmailModal.types';
import {
  convertDraftToFormValues,
  convertEmailDraftV2ToFormValues,
  convertFormValuesToEmailDraftV2,
} from './composeEmailModal.utils';

const formId = 'compose-email';

export const ComposeEmailModal = createAppModal(
  { type: 'app:composeEmail', featureFlag: 'email-compose-v2' },
  (props): JSX.Element => {
    const {
      isOpen,
      hide,
      modal: { arg: { draft, emailDraft } = {} } = {},
    } = props;

    const [createConversationEmail] =
      emailsApi.useCreateConversationEmailMutation();
    const { set: setUserStorage } = useServerStorage('user');

    // The timestamp identifies which server-storage draft is loaded; null means a fresh compose.
    const draftTimestamp = emailDraft?.timestamp ?? null;

    const form = useForm<FormValues>({
      defaultValues: {
        toEmail: [],
        fromEmail: null,
        cc: [],
        bcc: [],
        subject: '',
        attachments_v2: [],
        body: '',
        offerInQueue: false,
        queueId: null,
        tags: [],
        offer: false,
        // Include internal note is only used internally in the form
        includeInternalNote: false,
        internalNote: '',
        // Linked conversation
        linkType: null,
        parentConversation: null,
        ...convertDraftToFormValues(draft),
        ...(emailDraft ? convertEmailDraftV2ToFormValues(emailDraft) : {}),
      },
      mode: 'all',
    });
    const {
      control,
      formState: { isValid, isSubmitting },
      handleSubmit,
      watch,
      setValue,
    } = form;
    const ccEmails = watch('cc');
    const bccEmails = watch('bcc');
    const [ccEnabled, toggleCC] = useToggle(ccEmails.length > 0);
    const [bccEnabled, toggleBCC] = useToggle(bccEmails.length > 0);

    const showInternalNote = watch('includeInternalNote');
    const queue = watch('queueId');
    const offerInQueue = watch('offerInQueue');

    const onSubmit = async (values: FormValues) => {
      if (!values.fromEmail?.[0]) return;

      await createConversationEmail({
        from_email: values.fromEmail[0],
        to_email: values.toEmail[0].email,
        subject: values.subject,
        body: values.body,
        content_type: 'html',
        ...(values.attachments_v2.length
          ? {
              attachments_v2: values.attachments_v2.map((a) => ({
                pretty_name: a.prettyName,
                url: a.url,
              })),
            }
          : {}),
        ...(values.cc.length ? { cc_emails: values.cc } : {}),
        ...(values.bcc.length ? { bcc_emails: values.bcc } : {}),
        ...(values.tags.length ? { tags: values.tags.map((t) => t.name) } : {}),
        ...(values.internalNote ? { internal_note: values.internalNote } : {}),
        ...(values.queueId?.length ? { queue_id: values.queueId[0] } : {}),
        ...(values.offerInQueue ? { offer: true } : {}),
        ...(values.parentConversation
          ? {
              link: {
                parent_csid: values.parentConversation,
                ...(values.linkType ? { link_type: values.linkType } : {}),
              },
            }
          : {}),
      }).unwrap();

      if (draftTimestamp !== null) {
        setUserStorage({
          drafts: (storage) => {
            if (!storage.email) return;
            const idx = storage.email.findIndex(
              (d) => d.timestamp === draftTimestamp,
            );
            if (idx !== -1) storage.email.splice(idx, 1);
          },
        });
      }

      hide();
    };

    const handleSaveDraft = useCallback(() => {
      const values = form.getValues();
      const timestamp = draftTimestamp ?? Date.now();
      const newDraft = convertFormValuesToEmailDraftV2(values, timestamp);

      setUserStorage({
        drafts: (storage) => {
          const items = (storage.email ??= []);
          const existingIdx = items.findIndex((d) => d.timestamp === timestamp);
          if (existingIdx !== -1) {
            items[existingIdx] = newDraft;
          } else {
            items.push(newDraft);
          }
        },
      });

      hide();
    }, [draftTimestamp, form, setUserStorage, hide]);

    useEffect(() => {
      if (queue?.length === 0 && offerInQueue) {
        // If no queue is selected, offerInQueue must be false
        setValue('offerInQueue', false);
      }
    }, [queue, offerInQueue, setValue]);

    useEffect(() => {
      if (!ccEnabled) {
        // if CC is toggled off, clear the CC field
        setValue('cc', []);
      }
    }, [ccEnabled, setValue]);

    useEffect(() => {
      if (!bccEnabled) {
        // if BCC is toggled off, clear the BCC field
        setValue('bcc', []);
      }
    }, [bccEnabled, setValue]);

    const ccOnClick: MouseEventHandler<HTMLButtonElement> = useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleCC();
      },
      [toggleCC],
    );

    const bccOnClick: MouseEventHandler<HTMLButtonElement> = useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleBCC();
      },
      [toggleBCC],
    );

    return (
      <FormProvider {...form}>
        <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
          <ModalHead title="Compose new email" onClose={hide}></ModalHead>

          <ModalBody>
            <form onSubmit={handleSubmit(onSubmit)} id={formId}>
              <FormGrid>
                <FormField span={6}>
                  <ComposeEmailFieldEmailContact
                    name="toEmail"
                    label="To"
                    required
                    componentProps={{
                      suffix: {
                        type: 'custom',
                        noWrap: true,
                        value: (
                          <>
                            <Button
                              variant={ccEnabled ? 'primary' : 'tertiary'}
                              className={styles.suffixButton}
                              onClick={ccOnClick}
                            >
                              CC
                            </Button>
                            <Button
                              variant={bccEnabled ? 'primary' : 'tertiary'}
                              className={styles.suffixButton}
                              onClick={bccOnClick}
                            >
                              BCC
                            </Button>
                          </>
                        ),
                      },
                    }}
                  />
                </FormField>

                {(ccEnabled || bccEnabled) && (
                  <FormField>
                    <FlexBox padding="250 400" className={styles.optionalBox}>
                      <FormGrid>
                        {ccEnabled && (
                          <>
                            <FormField span={6}>
                              <ComposeEmailFieldEmailContact
                                name="cc"
                                label="CC"
                                multiple
                              />
                            </FormField>
                          </>
                        )}
                        {bccEnabled && (
                          <>
                            <FormField span={6}>
                              <ComposeEmailFieldEmailContact
                                name="bcc"
                                label="BCC"
                                multiple
                              />
                            </FormField>
                          </>
                        )}
                      </FormGrid>
                    </FlexBox>
                  </FormField>
                )}

                <FormField>
                  <HookFormEmailSelect
                    control={control}
                    name="fromEmail"
                    label="From"
                  />
                </FormField>

                <FormField>
                  <HookFormText
                    name="subject"
                    control={control}
                    label="Subject"
                  />
                </FormField>

                <FormField>
                  <ComposeEmailFieldRichText
                    name="body"
                    label="Message"
                    placeholder="Type a message"
                    required
                  />
                </FormField>

                <FormField>
                  <Box margin="0 0 300" width="100%">
                    <ToggleBox
                      className={styles.optionalBox}
                      isOpen={showInternalNote}
                      headComponent={ToggleBoxHead}
                      title="Internal note"
                      helpIcon={undefined}
                      input={{
                        id: 'internal-note-toggle-box',
                        name: 'internal-note-toggle-box',
                        value: 'internal-note-toggle-box',
                      }}
                      onChange={(open) => setValue('includeInternalNote', open)}
                    >
                      <ToggleBoxBody>
                        {showInternalNote && (
                          <FormField>
                            <ComposeEmailFieldRichText
                              name="internalNote"
                              label="Internal note"
                              placeholder="Type an internal note"
                            />
                          </FormField>
                        )}
                      </ToggleBoxBody>
                    </ToggleBox>
                  </Box>
                </FormField>

                <FormField>
                  <HookFormTagSelect
                    control={control}
                    label="Tags"
                    name="tags"
                  />
                </FormField>

                <FormField span={4}>
                  <HookFormQueuesSelect
                    control={control}
                    label="Queue"
                    name="queueId"
                    componentProps={{
                      placeholder: 'Select a queue',
                      clearable: true,
                    }}
                  />
                </FormField>

                <FormField span={2}>
                  <FieldWrapper label="">
                    <FlexBox alignItems="center" height="40px">
                      <HookFormCheckbox
                        control={control}
                        label="Offer in queue"
                        name="offerInQueue"
                        componentProps={{
                          disabled: queue?.length === 0,
                        }}
                        helpIcon={{
                          text: (
                            <>
                              <strong>If checked</strong>, the conversation will
                              be offered to agents in the selected queue.
                              <br />
                              <br />
                              <strong>If unchecked</strong>, the conversation
                              will be added to the queue in a closed state, and
                              it will not be offered to any agent.
                              <br />
                              <br />
                              <strong>If no queue is selected</strong>, offer in
                              queue is not available.
                            </>
                          ),
                        }}
                      />
                    </FlexBox>
                  </FieldWrapper>
                </FormField>
              </FormGrid>
            </form>
          </ModalBody>

          <ModalFoot
            cancelName="Cancel"
            cancelOnClick={hide}
            confirmSecondaryName="Save draft"
            confirmSecondaryOnClick={handleSaveDraft}
            confirmName="Send"
            confirmOnClick={() => undefined}
            confirmProps={{
              type: 'submit',
              disabled: !isValid || isSubmitting,
              isLoading: isSubmitting,
              loadingIndicator: 'Sending...',
              form: formId,
            }}
          />
        </Modal>
      </FormProvider>
    );
  },
);
